"use client";
import React, { useState, useEffect } from 'react';
import { CarouselBlockInterface } from "@/types/strapi-dynamic";
import CarouselComponent from "../Components/C-Carousel-Component";
import { StrapiImage } from "@/comp/_Utility/StrapiImage";

export default function CarouselBlock(blockData: CarouselBlockInterface) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const components = blockData.blockComponents || [];
    const length = components.length;

    // Handle Auto-Turn logic
    useEffect(() => {
        if (!blockData.autoTurn || length <= 1) return;

        // Normalize turnTime: if user enters '5' (seconds), convert to 5000ms. Default to 5s.
        const time = blockData.turnTime
            ? (blockData.turnTime < 100 ? blockData.turnTime * 1000 : blockData.turnTime)
            : 5000;

        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % length);
        }, time);

        return () => clearInterval(timer);
    }, [blockData.autoTurn, blockData.turnTime, length]);

    if (length === 0) return null;

    // 6 predefined colors reusing the menu block palette
    const colorPalette = [
        { bg: "bg-[#fe4848]", text: "text-white" },       // pg-red
        { bg: "bg-[#4747ff]", text: "text-white" },       // pg-blue
        { bg: "bg-[#fcfc4a]", text: "text-gray-900" },    // pg-yellow
        { bg: "bg-[#1b1464]", text: "text-white" },       // pg-purple
        { bg: "bg-[#d9f99d]", text: "text-gray-900" },    // light lime
        { bg: "bg-[#ffedd5]", text: "text-gray-900" }     // soft orange
    ];

    return (
        <section className="w-full my-6 lg:px-0 max-w-full mx-auto relative">

            {/* Top/Bottom-left Corner Overlay: Renders Logo OR Heading */}
            <div className="absolute bottom-20 left-8 lg:bottom-16 lg:left-12 z-20 pointer-events-none">
                {blockData.logoOverlay && blockData.blockImage?.url ? (
                    <StrapiImage
                        media={blockData.blockImage}
                        quality="medium"
                        className="w-auto h-20 md:h-28 lg:h-36 object-contain drop-shadow-2xl backdrop-blur-xs"
                    />
                ) : blockData.blockHeading ? (
                    <h2 className="text-3xl md:text-7xl font-medium text-white drop-shadow-xl tracking-tight bg-black/30 backdrop-blur-xs px-6 py-3">
                        {blockData.blockHeading}
                    </h2>
                ) : null}
            </div>

            <div className="relative overflow-hidden rounded-sm shadow-xl bg-gray-100 flex flex-col group">

                {/* Slides Track */}
                <div
                    className="flex transition-transform duration-700 ease-in-out w-full h-[50vh] md:h-[75vh]"
                    style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                    {components.map((comp, index) => {
                        const color = colorPalette[index % colorPalette.length];
                        return (
                            <CarouselComponent key={comp.id} data={comp} color={color} />
                        );
                    })}
                </div>

                {/* Manual Navigation Arrows (Appears on hover) */}
                {length > 1 && (
                    <>
                        <button
                            onClick={() => setCurrentIndex((prev) => (prev - 1 + length) % length)}
                            className="absolute left-4 top-[40%] md:top-[45%] z-10 p-3 rounded-full  text-white backdrop-blur-md shadow-md"
                            aria-label="Previous Slide"
                        >
                            ◀
                        </button>
                        <button
                            onClick={() => setCurrentIndex((prev) => (prev + 1) % length)}
                            className="absolute right-4 top-[40%] md:top-[45%] z-10 p-3 rounded-full text-white backdrop-blur-md shadow-md"
                            aria-label="Next Slide"
                        >
                            ▶
                        </button>

                        {/* Slide Indicators - Positioned exactly between image and lower bar */}

                    </>
                )}
            </div>
        </section>
    );
}
